import { useQuery } from "react-query";

import { zodParseWithLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

import {
    GeographyArraySchema,
    geographySearchApiKey,
    sortOriginGeographyOptions,
    type Geography,
} from "./geographySchema";

const ORIGIN_GEOGRAPHY_TYPES = new Set<Geography["type"]>(["country", "region"]);
const ORIGIN_GEOGRAPHY_PAGE_SIZE = 200;

type UseOriginGeographyOptions = {
    enabled?: boolean;
};

const fetchGeographiesByType = async (type: Geography["type"]): Promise<Geography[]> => {
    const response = await loggedInApiCall.get<{ "hydra:member": unknown }>("/api/geographies", {
        params: {
            type,
            itemsPerPage: ORIGIN_GEOGRAPHY_PAGE_SIZE,
            page: 1,
        },
    });

    if (!response.data?.["hydra:member"]) {
        return [];
    }

    return zodParseWithLog(response.data["hydra:member"], GeographyArraySchema, `originGeographyOptions:${type}`);
};

export const useOriginGeographyOptions = ({ enabled = true }: UseOriginGeographyOptions = {}) => {
    const query = useQuery({
        queryKey: [geographySearchApiKey, "origin-options"],
        queryFn: async () => {
            const [regions, countries] = await Promise.all([
                fetchGeographiesByType("region"),
                fetchGeographiesByType("country"),
            ]);

            return sortOriginGeographyOptions(
                [...regions, ...countries].filter((geography) => ORIGIN_GEOGRAPHY_TYPES.has(geography.type)),
            );
        },
        enabled,
        staleTime: 60 * 60 * 1000,
        cacheTime: 60 * 60 * 1000,
    });

    return {
        geographies: query.data ?? [],
        ...query,
    };
};
